import { faSpinner, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ImageList, ImageListItem, ImageListItemBar } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import DeleteStoppageImageButton from "./DeleteStoppageImageButton";
import { StoppageDto } from "../../../DTOs/Stoppage/StoppageDto";
import { StoppageImageDTO } from "../../../DTOs/Stoppage/StoppageImageDTO";
import { stoppagesAjax } from "../../../ajax/StoppagesAjax";
import { useGlobalModal } from "../../../components/Contexts/GlobalModalContext";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { RootState } from "../../../state/store";

type StoppageImagesModalProps = {
  stoppage: StoppageDto;
  onStoppageUpdated?: (updated: StoppageDto) => void;
}

const StoppageImagesModal: React.FC<StoppageImagesModalProps> = ({ stoppage, onStoppageUpdated }) => {
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const { enqueueSnackbar } = useSnackbar();
  const { hideModal } = useGlobalModal();
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const [images, setImages] = useState<StoppageImageDTO[]>([]);
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);

  const fetchImages = useCallback(async () => {
    if (!stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 })
      return;
    }

    try {
      setIsWorking(true);

      const imagesResponse = await stoppagesAjax.getStoppageImages(stoppage.stoppageId);

      if (!ErrorResponse.isErrorResponse(imagesResponse)) {
        setImages(imagesResponse.data);
      }
      setIsWorking(false);

    } catch (e) {
      // Ignore
    }
  }, [stoppage.stoppageId, enqueueSnackbar]);

  const handleFileUpload = useCallback(async (stoppage: StoppageDto, file?: File) => {
    if (!file || !stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await stoppagesAjax.uploadStoppageImage(stoppage.stoppageId, formData);

      if (!response.success || !response.data) {
        enqueueSnackbar('Error uploading image, please try again', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      onStoppageUpdated?.(response.data);
      enqueueSnackbar('Image uploaded successfully', { variant: 'success', autoHideDuration: 3000 });
    } catch (error) {
      enqueueSnackbar('Error uploading image, please try again', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [onStoppageUpdated, enqueueSnackbar]);

  const handleFileDelete = useCallback(async (imageId: number) => {
    if (!imageId) {
      enqueueSnackbar('Invalid image selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    try {
      const response = await stoppagesAjax.deleteStoppageImage(imageId);

      if (!response.success || !response.data) {
        enqueueSnackbar('Error deleting image, please try again', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      onStoppageUpdated?.(response.data);
      enqueueSnackbar('Image deleted successfully', { variant: 'success', autoHideDuration: 3000 });    } catch (error) {
      enqueueSnackbar('Error deleting image, please try again', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [onStoppageUpdated, enqueueSnackbar]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  return (
    <div>
      <div className="flex flex-col items-center">
        <h2 className="border-b border-theme-heading-border font-semibold pb-2 mb-8 short:mb-4 text-[1.6rem] short:text-[1.2rem] text-theme-text">
          Stoppage Images
        </h2>
        <div
          className="h-[32rem] relative w-full flex items-center justify-center border border-theme-heading-border rounded">
          <div
            className={`absolute inset-0 flex items-center justify-center bg-theme-surface-2 transition-opacity duration-300 ${isWorking ? 'opacity-100 z-10' : 'opacity-0 pointer-events-none'}`}
          >
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className="text-theme-text text-8xl"
            />
          </div>

          <div
            className={`bg-theme-surface-2 transition-opacity duration-300 w-full h-full ${isWorking ? 'opacity-0' : 'opacity-100'}`}>
            {images.length === 0 ?
              <div
                className="flex flex-col gap-4 items-center justify-center h-full w-full text-theme-text text-lg">
                <span className="font-semibold text-2xl">No images found</span>
                <span>Upload Image</span>
                <Button
                  variant="outlined"
                  component="label"
                  classes={{ root: 'h-[3rem] w-[10rem] flex items-center gap-2 !bg-theme-surface' }}
                  size="small"
                  sx={{ padding: 0.8 }}
                  disabled={isFileUploading || isShiftLocked}
                >
                  <span className="absolute flex items-center justify-center text-theme-text">
                    <FontAwesomeIcon
                      icon={faUpload}
                      className={`text-lg transition-opacity duration-300 ease-in-out ${isFileUploading ? 'opacity-0' : 'opacity-100'}`}
                    />
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      className={`text-lg absolute left-0 transition-opacity duration-300 ease-in-out ${isFileUploading ? 'opacity-100' : 'opacity-0'}`}
                    />
                  </span>
                  <input
                    type="file"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setIsFileUploading(true);
                        await handleFileUpload(stoppage, file);
                        setIsFileUploading(false);
                        await fetchImages();
                      }
                    }} />
                </Button>
              </div>
              :
              <ImageList
                className="w-full h-full overflow-auto"
                cols={images.length > 1 ? 2 : 1}
              >
                {images.map(image => (
                  <ImageListItem
                    key={image.stoppageImageId}
                    className="flex items-center gap-4 shadow-md"
                  >
                    <img
                      src={`data:${image.contentType};base64,${image.imageData}`}
                      alt={image.fileName}
                      className="min-h-[18rem] rounded"
                    />
                    <ImageListItemBar
                      sx={{
                        background:
                          'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                          'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                      }}
                      title=""
                      position="top"
                      actionIcon={
                        <DeleteStoppageImageButton
                          fileName={image.fileName || ''}
                          handleFileDelete={async () => {
                            await handleFileDelete(image.stoppageImageId);
                            await fetchImages();
                          }}
                        />
                      }
                      actionPosition="left"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            }
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-12 short:mt-4">
        {images.length !== 0 && <Button
          variant="outlined"
          component="label"
          classes={{ root: 'h-[3rem] w-[10rem] flex items-center gap-2 !bg-theme-surface' }}
          size="small"
          sx={{ padding: 0.8 }}
          disabled={isFileUploading || isShiftLocked}
        >
          <span className="absolute flex items-center justify-center text-theme-text">
            <FontAwesomeIcon
              icon={faUpload}
              className={`text-lg transition-opacity ease-in-out ${isFileUploading ? 'opacity-0' : 'opacity-100'}`}
            />
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className={`text-lg absolute left-0 transition-opacity ease-in-out ${isFileUploading ? 'opacity-100' : 'opacity-0'}`}
            />
          </span>
          <input
            type="file"
            hidden
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                setIsFileUploading(true);
                await handleFileUpload(stoppage, file);
                setIsFileUploading(false);
                await fetchImages();
              }
            }} />
        </Button>
        }
        <button
          type="button"
          onClick={hideModal}
          className="px-4 py-2 bg-theme-text-muted text-white cursor-pointer hover:bg-theme-text-muted-hover hover:text-theme-text-hover transition ease-out rounded-sm border-none"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default StoppageImagesModal;